<template>
	<v-dialog :value="isShow" :max-width="550" persistent>
		<MyAnnouncementCard
			v-if="announcementDisplay"
			:announcement="announcementDisplay"
			@markRead="onMarkRead"
			@remindLater="nextTime"
			@clearCache="onClearCache"
			@appUpdate="onAppUpdate"
		/>
	</v-dialog>
</template>

<script>
import loader from '@/components/loader'
import MyAnnouncementCard from '@/views/announcement/components/AnnouncementCard.vue'

export default {
	name: 'MyAnnouncementDisplayModal',
	components: { MyAnnouncementCard },
	data() {
		return {
			isShow: false,
			num: null,
			announcementListRequestSeq: 0,
		}
	},
	computed: {
		displayableAnnouncements() {
			return this.$store.getters['storage/getDisplayableAnnouncements']
		},
		unshownAnnouncements() {
			return this.$store.getters['storage/getUnshownAnnouncements']
		},
		announcementDisplay() {
			return this.displayableAnnouncements.find(v => v.id === this.num) || null
		},
		isReactNative() {
			return this.$isReactNative()
		},
		isMac() {
			return /macintosh|mac os x/i.test(navigator.userAgent)
		},
	},
	watch: {
		'$store.state.storage.announcementDisplayShow'() {
			const announcementDisplayShow =
				this.$store.state.storage.announcementDisplayShow
			if (announcementDisplayShow.id) {
				this.getAnnouncementDisplayList(announcementDisplayShow.id)
			} else if (
				announcementDisplayShow.status.toString()?.slice(0, 7) === 'REFRESH'
			) {
				this.getAnnouncementDisplayList()
			} else if (announcementDisplayShow.status === 'MANUAL') {
				this.showManually()
			} else {
				this.show()
			}
		},
		displayableAnnouncements() {
			this.syncModalWithDisplayableList()
		},
		announcementDisplay(val) {
			// 一旦某則公告實際顯示在畫面上就標記為已顯示，本次 session 內不再自動彈
			if (val && this.isShow) {
				this.$store.commit('storage/addShownAnnouncement', val.id)
			}
		},
	},
	methods: {
		syncModalWithDisplayableList() {
			if (!this.isShow || this.announcementDisplay) {
				return
			}
			const next = this.unshownAnnouncements[0]
			if (next) {
				this.num = next.id
			} else {
				this.hide()
			}
		},
		getAnnouncementDisplayList(id) {
			const requestSeq = ++this.announcementListRequestSeq
			this.$http.storeApiRouter.announcement
				.getAnnouncementDisplayList({
					...this.$getStoreId(),
				})
				.then(res => {
					if (requestSeq !== this.announcementListRequestSeq) {
						return
					}
					this.$store.commit('storage/setAnnouncementDisplayList', res.data)
					const found =
						id &&
						this.displayableAnnouncements.some(
							announcement => announcement.id === id,
						)

					if (found) {
						this.show(id)
					} else if (id) {
						this.hide()
					}
				})
		},
		show(id) {
			if (id) {
				const same = this.displayableAnnouncements.find(v => v.id === id)
				this.num = same ? same.id : this.unshownAnnouncements[0]?.id ?? null
			} else {
				this.num = this.unshownAnnouncements[0]?.id ?? null
			}
			this.isShow = !!this.announcementDisplay
		},
		// 手動點開（pageTitle 公告鈕）：顯示任一可顯示公告，不受「已顯示過」限制，
		// 讓按過「再提醒」的公告仍能主動叫回來
		showManually() {
			this.num = this.displayableAnnouncements[0]?.id ?? null
			this.isShow = !!this.announcementDisplay
		},
		hide() {
			this.isShow = false
		},
		nextTime() {
			// 目前這則於顯示時已被標記為已顯示，直接前進到下一則尚未顯示的公告
			const next = this.unshownAnnouncements[0]
			if (next) {
				this.num = next.id
			} else {
				this.hide()
			}
		},
		onMarkRead() {
			this.markCurrentRead()
			this.nextTime()
		},
		markCurrentRead() {
			if (this.announcementDisplay) {
				this.$store.commit(
					'storage/addHiddenAnnouncement',
					this.announcementDisplay,
				)
			}
		},
		onClearCache() {
			this.markCurrentRead()
			this.hide()
			if (this.isReactNative && !this.isMac) {
				window.ReactNativeWebView?.postMessage(
					JSON.stringify({ type: 'clearCache' }),
				)
			} else {
				// 下一行會整頁重載，先清除其他 token 並立即顯示；重載後遮罩自然消失。
				loader.clearAll()
				loader.show('清除緩存中，請稍候...', { immediate: true })
				window.location.href = window.location.href + '?reload=true'
			}
		},
		onAppUpdate() {
			// AnnouncementCard 的「前往更新」鈕僅在 app 環境顯示，非 app 不會觸發此事件
			if (this.isMac) {
				this.verifyVersionOtherwiseJumpURL(
					'APP_VERSION_IOS',
					this.$store.state.storage.iosVersion,
					this.announcementDisplay.iosAppUrl,
				)
			} else {
				this.verifyVersionOtherwiseJumpURL(
					'APP_VERSION_ANDROID',
					this.$store.state.storage.androidVersion,
					this.announcementDisplay.androidAppUrl,
				)
			}
		},
		verifyVersionOtherwiseJumpURL(key, currentVersion, link) {
			this.$http.storeApiRouter.platformParam
				.get(key)
				.then(res => {
					const onlineVersion = res.data
					const match = onlineVersion === currentVersion
					if (match) {
						// 版號相等 視同知道了
						this.markCurrentRead()
						this.nextTime()
						this.$store.commit('showMessage', '當前版號已是最新')
					} else {
						// 跳轉網址
						window.ReactNativeWebView?.postMessage(
							JSON.stringify({ type: 'link', data: link }),
						)
					}
				})
				.catch(_ => {
					// 異常視同再提醒
					this.nextTime()
				})
		},
	},
}
</script>
